import cc from "classcat"
import React, { useState } from "react"
import { useFormContext } from "react-hook-form"
import { graphql, useFragment } from "react-relay"

import styles from "../ProjectBudgetForm.module.css"

import { OtherExpenseEditRow_project$key } from "~/forms/ProjectBudgetForm/OtherExpenses/__generated__/OtherExpenseEditRow_project.graphql"

import { formatCurrency } from "~/helpers/CurrencyHelper"
import { OtherCost, addDerivedFields } from "~/helpers/otherCosts"

import CurrencyInput from "~/common/CurrencyInput"
import DateInput from "~/common/DateInput"
import Input from "~/common/Input"
import PhaseSelector from "~/common/PlannerDatePicker/PhaseSelector"
import { Delete } from "~/common/react-icons"

import { useProjectBudgetFormContext } from "../ProjectBudgetFormProvider"
import Column from "../TableColumn"
import Row from "../TableRow"
import { ProjectBudgetFormData } from "../formTypes"

type Props = {
  expenseIndex: number // Index in the form array
  project: OtherExpenseEditRow_project$key
  canDelete: boolean
}

const OtherExpenseEditRow = (props: Props) => {
  const { expenseIndex, canDelete = true } = props

  const { watch, setValue } = useFormContext<ProjectBudgetFormData>()
  const formData = watch()
  const { showNonBillableView } = useProjectBudgetFormContext()

  const project = useFragment<OtherExpenseEditRow_project$key>(
    graphql`
      fragment OtherExpenseEditRow_project on projects {
        id
        phases {
          id
          name
          color
        }
      }
    `,
    props.project,
  )

  // Get the expense from form data
  const expense = formData.otherExpenses[expenseIndex]
  const [error, setError] = useState(false)

  if (!expense) return null

  const isPhaseMode = formData.budgetMethod === "PHASE"
  const expenseWithCalculations = addDerivedFields(expense)
  const { margin, charge, profit } = expenseWithCalculations

  const handleItemNameRequired = (newExpense: OtherCost) => {
    setError(!newExpense.name || newExpense.name === "")
  }

  const updateExpense = (updates: Partial<OtherCost>) => {
    const updatedExpenses = [...formData.otherExpenses]
    const updatedExpense = { ...expense, ...updates }
    updatedExpenses[expenseIndex] = updatedExpense
    setValue("otherExpenses", updatedExpenses)
    handleItemNameRequired(updatedExpense)
  }

  const handleInputChange = (value: any, field: keyof OtherCost) => {
    updateExpense({ [field]: value })
  }

  // Date change needs its own handler because the various ways of interacting
  // with it make the blur event on its own unreliable
  const handleDateChange = (value: Date) => {
    updateExpense({ date: value })
  }

  const handlePhaseChange = (phase: { value: number }) => {
    updateExpense({ phaseId: phase.value })
  }

  const handleRowUpdate = () => {
    let updates: Partial<OtherCost> = {}

    if (isNaN(expense.cost + expense.charge) || !expense.date) {
      updates = {
        cost: expense.cost ?? 0,
        charge: expense.charge ?? 0,
        date: expense.date ?? new Date(),
      }
      updateExpense(updates)
    }
    handleItemNameRequired({ ...expense, ...updates })
  }

  const handleRemove = () => {
    if (!canDelete) return

    const updatedExpenses = formData.otherExpenses.filter(
      (_, index) => index !== expenseIndex,
    )
    setValue("otherExpenses", updatedExpenses)
  }

  const { id, date, name } = expense
  const CURRENCY_HEIGHT = 30

  return (
    <Row dataTest="other-expense-row">
      <Column>
        <Input
          aria-label="Item"
          value={name}
          onChange={(e) => handleInputChange(e.target.value, "name")}
          onBlur={handleRowUpdate}
          style={{ textAlign: "left" }}
          dataTest={`other-expense-name`}
          name={`other-expense-name-${id}`}
          className={cc([
            styles.otherExpensesTextInput,
            {
              [styles.errorBorder]: error,
            },
          ])}
        />
      </Column>
      {isPhaseMode && (
        <Column>
          <PhaseSelector
            currentPhase={expense.phaseId}
            phases={project.phases}
            onPhaseChange={handlePhaseChange}
            inputHeight={30}
            width={140}
          />
        </Column>
      )}
      <Column>
        <DateInput
          textAlignRight
          canClearSelection={false}
          value={date}
          aria-label="Date"
          dateFormat="d MMM yy"
          onChange={handleDateChange}
          inputProps={{
            name: `other-expense-date-${id}`,
            onBlur: handleRowUpdate,
            style: { borderColor: "var(--winter)" },
          }}
          className={styles.otherExpensesDateInput}
          dataTest={`other-expense-date`}
        />
      </Column>

      {!showNonBillableView && (
        <Column textAlign="right">
          <span data-test={`other-expense-margin`}>{margin}%</span>
        </Column>
      )}
      {!showNonBillableView && (
        <Column textAlign="right">
          <span data-test={`other-expense-profit`}>
            {formatCurrency(profit)}
          </span>
        </Column>
      )}
      <Column>
        <CurrencyInput
          value={expense.cost}
          aria-label="Unit Cost"
          onChange={(val) => handleInputChange(val, "cost")}
          onBlur={handleRowUpdate}
          min={0}
          height={CURRENCY_HEIGHT}
          dataTest="other-expense-cost"
          name={`other-expense-cost-${id}`}
          selectAllOnFocus
        />
      </Column>
      {!showNonBillableView && (
        <Column>
          <CurrencyInput
            value={charge}
            aria-label="Unit Charge"
            onChange={(val) => handleInputChange(val, "charge")}
            onBlur={handleRowUpdate}
            min={0}
            height={CURRENCY_HEIGHT}
            dataTest="other-expense-charge"
            name={`other-expense-charge-${id}`}
            selectAllOnFocus
          />
        </Column>
      )}
      <div
        data-test={`other-expense-delete`}
        onClick={canDelete ? handleRemove : null}
        className={cc([
          styles.deleteIconContainer,
          {
            [styles.disabled]: !canDelete,
          },
        ])}
      >
        <Delete />
      </div>
    </Row>
  )
}

export default OtherExpenseEditRow
